import { TRACKER_IDS, TRACKER_NAMES, type CasinoRow, type MetricKey, type Payload, type TrackerId, type WindowKey } from '../api/types';
import { median, pct, usd } from '../lib/format';
import { finite } from '../ui/charts';
import { AgreeMatrix, Bump, DevSwarm, Emblem, Overlap, type PairStat } from '../ui/compare';
import { CardHead, GlassHBars } from '../ui/glass';
import { Icon } from '../ui/Icon';

/* The four trackers against each other: who covers what, who reads high or
   low, how often each pair agrees, whether they even rank casinos the same
   way, what each one publishes at all, and where they state different facts
   about the same casino. Every comparison is made on casinos both sides list;
   a casino one tracker leaves out is never read as a zero. */

const WINDOWS: WindowKey[] = ['1d', '7d', '14d', '30d', '90d', '365d'];
const WLABEL: Record<WindowKey, string> = { '1d': '24h', '7d': '7d', '14d': '14d', '30d': '30d', '90d': '90d', '365d': '1y' };
const WLONG: Record<WindowKey, string> = { '1d': '24 hours', '7d': '7 days', '14d': '14 days', '30d': '30 days', '90d': '90 days', '365d': '1 year' };

/** Percent from the casino's median, for any tracker, where two or more counted trackers set that median. */
const devOf = (r: CasinoRow, id: TrackerId): number | null => {
  const v = r.by[id]?.value;
  return finite(v) && r.consensusBy.length >= 2 && finite(r.consensus) && r.consensus > 0 ? ((v - r.consensus) / r.consensus) * 100 : null;
};

const leanText = (d: number | null) =>
  !finite(d) ? 'no shared casinos' : Math.abs(d) < 1 ? 'on the median' : `${Math.abs(d).toFixed(1)}% ${d < 0 ? 'below' : 'above'}`;

/** The stale-feed age out of the source's scope note, for a one-line reason on the card. */
const staleDays = (scope: string | undefined) => { const m = scope?.match(/(\d+)\s+days?\s+ago/); return m ? Number(m[1]) : null; };

/* What a tracker publishes, measured across every window: the most casinos
   any one window carries that figure for. */
type Feature = { label: string; count: (id: TrackerId) => number };

export function CompareAll({ data, win }: { data: Payload; win: WindowKey }) {
  // Only 7d and 30d carry every tracker. If the chosen window has fewer than
  // two, compare on the window with the most trackers rather than show nothing.
  const vols = data.analysis.volume;
  const best = [...WINDOWS].sort((a, b) => (vols[b]?.sources.length ?? 0) - (vols[a]?.sources.length ?? 0) || (a === '7d' ? -1 : b === '7d' ? 1 : 0))[0] as WindowKey;
  const cw: WindowKey = (vols[win]?.sources.length ?? 0) >= 2 ? win : best;
  const v = vols[cw];
  if (!v) return <div className="tile"><p className="empty">Nothing published to compare.</p></div>;

  const ids = TRACKER_IDS.filter((id) => v.sources.includes(id));
  const counted = v.consensusSources;
  const rows = v.rows;
  const status = (id: TrackerId) => data.sources.find((s) => s.id === id);

  // --- lean against the median
  const devs = Object.fromEntries(ids.map((id) => [id, rows.flatMap((r) => { const d = devOf(r, id); return finite(d) ? [{ key: r.key, name: r.name, dev: d }] : []; })])) as Record<TrackerId, { key: string; name: string; dev: number }[]>;
  const leanOf = (id: TrackerId) => median((devs[id] ?? []).map((d) => d.dev));

  // --- pairwise agreement, on casinos both list
  const pairStat = (a: TrackerId, b: TrackerId): PairStat => {
    const ratios = rows.flatMap((r) => {
      const va = r.by[a]?.value, vb = r.by[b]?.value;
      return finite(va) && finite(vb) && va > 0 && vb > 0 ? [vb / va] : [];
    });
    return { shared: ratios.length, within: ratios.filter((x) => Math.abs(Math.log(x)) <= Math.log(1.1)).length, ratio: median(ratios) };
  };
  const listed = (id: TrackerId) => rows.filter((r) => finite(r.by[id]?.value)).length;

  // --- ranking: the largest casinos every counted tracker ranks
  const ranked = rows.filter((r) => counted.every((id) => finite(r.commonRank?.[id]))).slice(0, 8);

  // --- what each publishes, across all windows
  const across = (fn: (rs: CasinoRow[]) => number) => Math.max(0, ...WINDOWS.map((w) => (vols[w] ? fn(vols[w].rows) : 0)));
  const field = (...keys: MetricKey[]) => (id: TrackerId) => across((rs) => rs.filter((r) => keys.some((k) => finite(r.by[id]?.[k]))).length);
  const extra = (key: 'chains' | 'balances' | 'daily' | 'distribution') => (id: TrackerId) =>
    across((rs) => rs.filter((r) => { const x = r.extra?.[id]?.[key]; return Array.isArray(x) ? x.length > 0 : !!x; }).length);
  const profiles = data.analysis.profiles;
  const sentiment = data.analysis.sentiment?.rows ?? [];
  const SCORE: Partial<Record<TrackerId, 'circusScore' | 'fgScore'>> = { circus: 'circusScore', fairgambling: 'fgScore' };

  const GROUPS: { title: string; features: Feature[] }[] = [
    {
      title: 'Windows',
      features: WINDOWS.map((w) => ({ label: WLONG[w], count: (id: TrackerId) => (vols[w]?.sources.includes(id) ? vols[w]?.totals?.[id]?.casinos ?? 1 : 0) })),
    },
    {
      title: 'Money and players',
      features: [
        { label: 'Deposit volume', count: field('value') },
        { label: 'Withdrawals', count: field('withdrawals') },
        { label: 'Net flow', count: field('netFlow') },
        { label: 'Largest deposit', count: field('largestDeposit') },
        { label: 'Deposit count', count: field('count') },
        { label: 'Depositors', count: field('depositors') },
        { label: 'Average deposit', count: field('avgDeposit') },
        { label: 'New depositors', count: field('newDeposits', 'ftd') },
        { label: 'Returning players', count: field('returnRate') },
      ],
    },
    {
      title: 'Beyond volume',
      features: [
        { label: 'Market share', count: field('share') },
        { label: 'Change on last period', count: field('change', 'depositsChange') },
        { label: 'Chain split', count: extra('chains') },
        { label: 'Hot-wallet balances', count: extra('balances') },
        { label: 'Daily series', count: extra('daily') },
        { label: 'Deposit sizes', count: extra('distribution') },
        { label: 'Stated facts', count: (id) => (profiles?.rows ?? []).filter((r) => r.fields.some((f) => f.values[id] !== undefined && f.values[id] !== null)).length },
        { label: 'Own casino score', count: (id) => { const k = SCORE[id]; return k ? sentiment.filter((s) => finite(s[k])).length : 0; } },
      ],
    },
  ];
  const allFeatures = GROUPS.flatMap((g) => g.features);
  const published = (id: TrackerId) => allFeatures.filter((f) => f.count(id) > 0).length;

  // --- facts they state differently
  const factFields = (profiles?.fields ?? []).filter((f) => f.compare !== false && f.compared > 0).sort((a, b) => b.conflicts / b.compared - a.conflicts / a.compared);
  const volOf = new Map(rows.map((r) => [r.key, r.consensus ?? 0]));
  const conflicts = (profiles?.rows ?? [])
    .flatMap((r) => r.fields.filter((f) => f.agree === false).map((f) => ({ row: r, f })))
    .sort((a, b) => (volOf.get(b.row.key) ?? 0) - (volOf.get(a.row.key) ?? 0))
    .slice(0, 10);
  const factIds = TRACKER_IDS.filter((id) => (profiles?.sources ?? []).includes(id));
  const show = (x: string | number | boolean | null | undefined) => (x === undefined || x === null || x === '' ? '—' : typeof x === 'boolean' ? (x ? 'yes' : 'no') : String(x));

  const balances = data.analysis.balances;

  return (
    <div className="pgrid">
      {cw !== win && (
        <div className="banner span-12">
          <Icon name="info" />
          <span>Only {vols[win]?.sources.length ?? 0} tracker publishes a {WLONG[win]} window, and a comparison needs two. Showing <b>{WLONG[cw]}</b>, which {v.sources.length} trackers publish.</span>
        </div>
      )}

      {/* ------------------------------------------------------ scorecards */}
      {ids.map((id) => {
        const s = status(id);
        const total = v.totals?.[id];
        const lean = leanOf(id);
        const d = devs[id] ?? [];
        const bal = balances?.totals?.[id] as { value?: number; count?: number; casinos?: number } | undefined;
        const share = rows.length ? ((total?.casinos ?? 0) / rows.length) * 100 : 0;
        return (
          <section key={id} className={`tile span-3 tcard${counted.includes(id) ? '' : ' is-ref'}`} style={{ ['--tc' as string]: `var(--tracker-${id})` }}>
            <div className="tcard-head">
              <Emblem id={id} />
              <div>
                <h2>{TRACKER_NAMES[id]}</h2>
                <span className={`tcard-state${s?.ok && !s.stale ? '' : ' warn'}`}><i aria-hidden />{s?.ok ? (s.stale ? 'Stale feed' : 'Fresh, read hourly') : 'Failed last read'}</span>
              </div>
              <span className="tcard-role">{counted.includes(id) ? 'Counted' : 'Reference'}</span>
            </div>
            <div className="tcard-figure">
              <b>{usd(total?.value)}</b>
              <small>deposits over {WLONG[cw]}, across its own {total?.casinos ?? 0} casinos</small>
            </div>
            <div className="tcard-cover" title={`${total?.casinos ?? 0} of the ${rows.length} casinos any tracker lists this window`}>
              <span className="ghbar-track"><i style={{ width: `${Math.max(3, share)}%`, ['--gb-color' as string]: `var(--tracker-${id})` }} /></span>
              <small>lists {pct(share, 0)} of the {rows.length} casinos any tracker lists</small>
            </div>
            <ul className="tcard-kv">
              <li><b>{v.coverage?.[id]?.unique?.length ?? 0}</b><span>casinos only it lists</span></li>
              <li><b>{finite(lean) ? `${lean > 0 ? '+' : ''}${lean.toFixed(1)}%` : '—'}</b><span>{finite(lean) ? `vs the median, ${d.filter((x) => x.dev < -1).length} low · ${d.filter((x) => x.dev > 1).length} high` : 'no shared casinos'}</span></li>
              <li><b>{published(id)}<em>/{allFeatures.length}</em></b><span>kinds of figure published</span></li>
              <li><b>{finite(bal?.value) && bal.value > 0 ? usd(bal.value) : '—'}</b><span>{finite(bal?.count) && bal.count > 0 ? `in ${bal.count} hot wallets watched` : finite(bal?.value) && bal.value > 0 ? 'in hot wallets, count not stated' : 'no hot-wallet data'}</span></li>
            </ul>
            {!counted.includes(id) && (
              <p className="tcard-note" title={s?.scope ?? undefined}>
                Kept out of the median{finite(staleDays(s?.scope ?? undefined)) ? `: its feed was last updated ${staleDays(s?.scope ?? undefined)} days ago` : ''}.
              </p>
            )}
          </section>
        );
      })}

      {/* ----------------------------------------------------------- lean */}
      <section className="tile span-12">
        <CardHead icon="gap" tone="amber" title="Who reads high, who reads low" note={`Each dot is a casino: how far this tracker's figure sits from the casino's median, ${WLONG[cw]}. The capsule holds the middle half.`} />
        <DevSwarm rows={ids.map((id) => ({
          id, counted: counted.includes(id), points: devs[id] ?? [],
          summary: `typically ${leanText(leanOf(id))} · ${(devs[id] ?? []).length} casinos`,
        }))} />
        <p className="tile-foot">
          The median is taken over the counted trackers, so a counted tracker is part of what it is measured against; a tracker that sets the median sits at 0.
          {ids.some((id) => !counted.includes(id)) ? ' * Reference only: measured against the median but never part of it.' : ''} The axis is compressed toward the ends so casinos 5% out and 150% out both stay readable.
        </p>
      </section>

      {/* ------------------------------------------------ agreement, overlap */}
      <section className="tile span-6">
        <CardHead icon="rank" tone="blue" title="How often each pair agrees" note="Share of the casinos both list where their figures are within 10%. Open a pair to see every casino." />
        <AgreeMatrix ids={ids} stat={pairStat} listed={listed} href={(a, b) => `/pair?x=${a}&y=${b}&w=${cw}`} />
        <div className="ramp-key" aria-hidden>
          <span>fewer within 10%</span>
          <i className="a0" /><i className="a1" /><i className="a2" /><i className="a3" /><i className="a4" />
          <span>more</span>
        </div>
      </section>

      <section className="tile span-6">
        <CardHead icon="table" tone="violet" title="Who lists which casinos" note="Casinos grouped by exactly which trackers list them. A bar in a tracker's colour is casinos only it lists." />
        <Overlap ids={ids} sets={rows.map((r) => r.reportedBy)} />
        <p className="tile-foot">{v.commonCount ?? 0} casinos are on every counted tracker. Those are the only ones where a total can be compared fairly.</p>
      </section>

      {/* ------------------------------------------------ ranking, facts */}
      <section className="tile span-7">
        <CardHead icon="line" tone="blue" title="Same casinos, different order" note={`The ${ranked.length} largest casinos every counted tracker lists, ranked by each tracker. Crossing lines are where they disagree on order.`} />
        {ranked.length > 1 && counted.length > 1
          ? <Bump ids={counted} lines={ranked.map((r) => ({ key: r.key, name: r.name, ranks: r.commonRank ?? {} }))} />
          : <p className="empty">Needs two counted trackers with casinos in common.</p>}
      </section>

      <section className="tile span-5">
        <CardHead icon="star" tone="magenta" title="Where they state different facts" note="Of the casinos where two or more trackers state a fact, how often they state different things." />
        {factFields.length
          ? <GlassHBars max={100} items={factFields.map((f) => {
              const p = (f.conflicts / f.compared) * 100;
              return {
                key: f.key, label: f.label, value: p, display: `${f.conflicts} of ${f.compared}`,
                sub: f.sources.map((s) => TRACKER_NAMES[s]).join(' · '),
                className: `g${p < 10 ? 0 : p < 30 ? 1 : p < 60 ? 2 : 3}`,
              };
            })} />
          : <p className="empty">No stated facts to compare.</p>}
        <p className="tile-foot">These are facts, not measurements: a casino has one founding year, so every conflict means at least one tracker has it wrong.</p>
      </section>

      {/* ------------------------------------------------ what each publishes */}
      <section className="tile span-12">
        <CardHead icon="size" tone="teal" title="What each one publishes" note="A tick where the tracker publishes that figure, with how many casinos it covers at most in any one window." />
        <div className="pubgrid">
          {GROUPS.map((g) => (
            <table key={g.title} className="pub">
              <thead>
                <tr><th>{g.title}</th>{TRACKER_IDS.map((id) => <th key={id} title={TRACKER_NAMES[id]}><Emblem id={id} size={24} /></th>)}</tr>
              </thead>
              <tbody>
                {g.features.map((f) => (
                  <tr key={f.label}>
                    <td>{f.label}</td>
                    {TRACKER_IDS.map((id) => {
                      const n = f.count(id);
                      return (
                        <td key={id} title={`${TRACKER_NAMES[id]}: ${n ? (g.title === 'Windows' ? `publishes ${f.label}` : `${n} casinos`) : 'not published'}`}>
                          {n ? <span className="tick" style={{ ['--tc' as string]: `var(--tracker-${id})` }}>{g.title === 'Windows' ? '' : n}</span> : <span className="no">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
        <ul className="pub-total">
          {TRACKER_IDS.map((id) => (
            <li key={id}><Emblem id={id} size={22} /><b>{published(id)}</b> of {allFeatures.length}<span>{TRACKER_NAMES[id]}</span></li>
          ))}
        </ul>
      </section>

      {/* ------------------------------------------------ conflicting facts */}
      {conflicts.length > 0 && (
        <section className="tile span-12">
          <CardHead icon="table" tone="violet" title="The same casino, described differently" note="The largest casinos where trackers state a fact differently." />
          <div className="t-wrap">
            <table className="t">
              <thead>
                <tr><th>Casino</th><th>Fact</th>{factIds.map((id) => <th key={id}><i className="gdot" style={{ background: `var(--tracker-${id})`, display: 'inline-block', marginRight: 8, width: 8, height: 8 }} aria-hidden />{TRACKER_NAMES[id]}</th>)}</tr>
              </thead>
              <tbody>
                {conflicts.map(({ row, f }) => (
                  <tr key={`${row.key}-${f.key}`} className="row-link">
                    <td><a className="plink" href={`/casino?c=${row.key}&w=${cw}`}>{row.name}</a></td>
                    <td>{f.label}</td>
                    {factIds.map((id) => <td key={id} className={f.values[id] === undefined ? 'muted' : ''}>{show(f.values[id])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="span-12 cmp-foot">Windows: {WINDOWS.filter((w) => vols[w]).map((w) => `${WLABEL[w]} (${vols[w]?.sources.length})`).join(' · ')}. Numbers in brackets are how many trackers publish each.</p>
    </div>
  );
}
